
  import { Injectable } from '@angular/core';
  import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
  import { Observable } from 'rxjs';
  import { catchError, map } from 'rxjs/operators';

  import { AppConstants } from '../app.constants';
  import { Student } from '../model/student.model';
  import { UserStats } from '../model/user-stats.model';
  import { StudentChecklist } from '../model/student-checklist.model';
  import { StudentDocument } from '../model/student-document.model';
  import { PartnerSummary } from '../model/partner-summary.model';
  import { UtilService } from './util.service';
  import { LoggerService } from './logger.service';
  import { ExceptionService } from './exception.service';
  import { LoaderEnabled } from './loader.service';

  @Injectable({
    providedIn: 'root'
  })
  export class StudentService {

    constructor(
      private http: HttpClient,
      private utilService: UtilService,
      private logger: LoggerService,
      private exceptionService: ExceptionService
    ) { }

  // ...existing methods continue below...

  public getAuditTrail(scaId: number): Observable<any> {
    const url = AppConstants.endpoint.student.audit_trail + '?scaId=' + scaId;
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public logStudentAction(scaId: number, studentId: number, action: string, details?: string): Observable<any> {
    let url = `${AppConstants.endpoint.student.students_journeys}/${scaId}/log-action?studentId=${studentId}&action=${encodeURIComponent(action)}`;
    if (details) {
      url += `&details=${encodeURIComponent(details)}`;
    }
    return this.http.put<any>(url, null, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getAuditTrailByStudentId(studentId: number): Observable<any> {
    const url = AppConstants.endpoint.student.audit_trail_by_student + '?studentId=' + studentId;
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getInteractionsByStudentId(studentId: number): Observable<any> {
    const url = AppConstants.endpoint.student.interaction_by_student.replace('{studentId}', String(studentId));
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public createStudentInteraction(payload: { studentId?: number; scaId?: number; companyId?: number; instituteId?: number; studentInteraction: string; time: string }): Observable<any> {
    const url = AppConstants.endpoint.student.interaction_base;
    return this.http.post<any>(url, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getInteractionsByScaId(scaId: number): Observable<any> {
    const url = AppConstants.endpoint.student.interaction_by_sca.replace('{scaId}', String(scaId));
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public createStudentInteractionBySca(payload: { studentId?: number; scaId?: number; companyId?: number; instituteId?: number; studentInteraction: string; time: string }): Observable<any> {
    const url = AppConstants.endpoint.student.interaction_base;
    return this.http.post<any>(url, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateStudentInteraction(id: number, payload: { studentId?: number; scaId?: number; companyId?: number; instituteId?: number; studentInteraction: string; time: string }): Observable<any> {
    const url = AppConstants.endpoint.student.interaction_update.replace('{id}', String(id));
    return this.http.put<any>(url, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteStudentInteraction(id: number): Observable<any> {
    const url = AppConstants.endpoint.student.interaction_delete.replace('{id}', String(id));
    return this.http.delete<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  // public getStudents(studentFilter: StudentFilter): Observable<Student[]> {
  //   let params = new HttpParams();
  //   if (studentFilter != null) {     
  //     if (studentFilter.name != null) {
  //       params = params.append('name', studentFilter.name);
  //     }
  //     if (studentFilter.email != null) {
  //       params = params.append('email', studentFilter.email);
  //     }
  //     if (studentFilter.citizenshipCountryId != null) {
  //       params = params.append('citizenshipCountryId', studentFilter.citizenshipCountryId.toString());
  //     }
  //     if (studentFilter.partnerId != null) {
  //       params = params.append('partnerId', studentFilter.partnerId.toString());
  //     }
  //   }

  //   return this.http.get<Student[]>(AppConstants.endpoint.student.search, {params: params})
  //     .pipe(catchError(this.exceptionService.handleError));
  // }

  /**
   * 
   START: Student Applications APIs
   */
  @LoaderEnabled()
  public getMyStudentApplications(instituteId: number, pageNumber: number, pageSize: number): Observable<any> {
    let params = new HttpParams()
      .append('pageNumber', pageNumber.toString())
      .append('pageSize', pageSize.toString());
    if (instituteId) {
      params = params.append('instituteId', instituteId.toString());
    }
    return this.http.get<any>(AppConstants.endpoint.student.my_applications, { params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public getStudentApplication(payload: any, instituteId: number): Observable<HttpResponse<Student[]>> {
    const studentFilter = payload.filter;
    const defaultParams = payload.defaultParams;
    let params = new HttpParams();
    if (studentFilter != null) {
      Object.keys(studentFilter).forEach(key => {
        params = params.append(key, studentFilter[key]?.toString());
      });
    }
    params = params.append('instituteId', instituteId);
    if (defaultParams) {
      params = params.append('pageNumber', defaultParams.currentPage.toString());
      params = params.append('pageSize', defaultParams.itemsPerPage.toString());
    }

    return this.http.get<Student[]>(`${AppConstants.endpoint.student.student_application_filter}`, { params: params, observe: 'response' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public getStudents(payload: any, instituteId: number): Observable<HttpResponse<Student[]>> {
    const studentFilter = payload.filter;
    const defaultParams = payload.defaultParams;
    let params = new HttpParams();
    if (studentFilter != null) {
      Object.keys(studentFilter).forEach(key => {
        params = params.append(key, studentFilter[key]?.toString());
      });
    }
    params = params.append('instituteId', instituteId);
    params = params.append('pageNumber', defaultParams.currentPage.toString());
    params = params.append('pageSize', defaultParams.itemsPerPage.toString());

    return this.http.get<Student[]>(`${AppConstants.endpoint.student.filter}`, { params: params, observe: 'response' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  createStudentApplication(payload: any, instituteId: number): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.student_application}?instituteId=${instituteId}`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentApplicationById(studentId: number): Observable<any> {
    return this.http.get<any>(`${AppConstants.endpoint.student.student_application}/${studentId}`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateStudentApplication(studentId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${AppConstants.endpoint.student.student_application}/${studentId}`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * 
   END: Student Applications APIs
   */

  /**
   * START: User Settings APIs
   */
  public getUserSettings(): Observable<any> {
    return this.http.get<any>(AppConstants.endpoint.user.settings)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateUserSettings(settings: object): Observable<any> {
    return this.http.put<any>(AppConstants.endpoint.user.settings, settings)
      .pipe(catchError(this.exceptionService.handleError));
  }
  /**
   * END: User Settings APIs
   */

  @LoaderEnabled()
  getStudentsByCourseDate(reqPayload: any, courseDate: number): Observable<any[]> {
    let params = new HttpParams();
    const filter = reqPayload.filter;
    const defaultParams = reqPayload.defaultParams;

    if (filter != null) {
      Object.keys(filter).forEach(key => {
        params = params.append(key, filter[key]?.toString());
      });

    }
    params = params.append('pageNumber', defaultParams.currentPage.toString());
    params = params.append('pageSize', defaultParams.itemsPerPage.toString());
    params = params.append('courseDateId', courseDate.toString());

    return this.http.get<any[]>(AppConstants.endpoint.student.students_journeys, { params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentDetailsByStudentId(studentId: number, courseDateId: number, scaId?: number): Observable<Student> {
    let url = `${AppConstants.endpoint.student.students_journeys}/${studentId}/${courseDateId}`;
    if (scaId) {
      url += `/${scaId}`;
    }
    return this.http.get<Student>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateStudentDetailsByStudentId(studentId: number, payload: Student): Observable<any> {
    return this.http.put<any>(`${AppConstants.endpoint.student.students_journeys}/${studentId}`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateStudentFeeDetails(studentId: number, courseDateId: number, payload: any): Observable<any> {
    return this.http.put<any>(`${AppConstants.endpoint.student.base}/${studentId}/${courseDateId}/student-fees`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  addStudent(value: any): Observable<any> {
    return this.http.post<any>(AppConstants.endpoint.student.students_journeys, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentPayments(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/getStudentPayments`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  saveStudentPayment(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/saveStudentPayment`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentFeeInstallments(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/getStudentCourseFee`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  saveStudentFeeInstallments(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/saveStudentCourseFee`, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  sendFeeReminder(payload: any): Observable<any> {
    return this.http.post<any>(AppConstants.endpoint.student.fee_reminder, payload, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentContracts(studentId: number, courseDateId: number): Observable<any> {

    return this.http.get<any>(`${AppConstants.endpoint.student.students_journeys}/get-student-contracts?courseDateId=${courseDateId}&studentId=${studentId}`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentAttachment(studentId: number, scaId?: number): Observable<any> {
    let url = `${AppConstants.endpoint.student.students_journeys}/document/${studentId}`;
    if (scaId) {
      url += `/${scaId}`;
    }
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * Creates a ZIP containing the selected student application documents.
   */
  @LoaderEnabled()
  downloadStudentDocumentsZip(attachmentIds: number[]): Observable<HttpResponse<Blob>> {
    return this.http.post(
      AppConstants.endpoint.student.download_student_documents_zip,
      { attachmentIds },
      { observe: 'response', responseType: 'blob' }
    ).pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  deleteStudentAttachment(attachmentId: number): Observable<string> {
    return this.http.delete(`${AppConstants.endpoint.student.students_journeys}/document/${attachmentId}`, { responseType: 'text' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * Toggle Do Not Disturb setting for document reminders
   * @param studentCourseApplicationId - The student course application ID
   * @param doNotDisturb - The new do not disturb value
   */
  @LoaderEnabled()
  toggleDoNotDisturb(studentCourseApplicationId: number, doNotDisturb: boolean): Observable<any> {
    return this.http.put(
      `${AppConstants.endpoint.student.students_journeys}/${studentCourseApplicationId}/toggle-do-not-disturb?doNotDisturb=${doNotDisturb}`,
      {},
      { responseType: 'text' }
    ).pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * Get document reminders / followup info for a student course application
   * @param studentCourseApplicationId - The student course application ID
   */
  @LoaderEnabled()
  getDocumentReminders(studentCourseApplicationId: number): Observable<any> {
    return this.http.get<any>(
      `${AppConstants.endpoint.student.students_journeys}/followup-info/${studentCourseApplicationId}`
    ).pipe(catchError(this.exceptionService.handleError));
  }


  @LoaderEnabled()
  uploadStudentContracts(payload: any, studentId: number): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/upload-student-contract/${studentId}?userRole=STUDENT`, payload, this.utilService.getMultiPartFormDataHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  uploadStudentAttachments(formData: any): Observable<any> {
    return this.http.post(`${AppConstants.endpoint.student.students_journeys}/upload-attachment`, formData, { responseType: 'text' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  downloadDocument(docId: number): Observable<any> {
    const url = this.utilService.prepareUrl(AppConstants.endpoint.file.download.document, docId);

    return this.http.get<any>(url, { responseType: 'blob' as 'json' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * START: Student Refund Fee Services
   */

  @LoaderEnabled()
  getStudentRefundFee(studentId: number, courseId: number): Observable<any> {
    return this.http.get<any>(`${AppConstants.endpoint.student.students_journeys}/getFeeRefund?studentId=${studentId}&courseId=${courseId}`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  saveStudentRefundFee(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/fee-refund`, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateStudentRefundFee(payload: any, feeStructureId: number): Observable<any> {
    return this.http.put<any>(`${AppConstants.endpoint.student.students_journeys}/fee-refund/${feeStructureId}`, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }
  @LoaderEnabled()
  addRefundFeePayment(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/add-payment`, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }
  @LoaderEnabled()
  getAllRefundPayments(studentId: number, courseId: number, scaId: number, type: string): Observable<any> {
    return this.http.get<any>(`${AppConstants.endpoint.student.students_journeys}/get-all-payments?studentId=${studentId}&courseId=${courseId}&scaId=${scaId}&type=${type}`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateStudentCourseApplicationStatus(studentCourseApplicationId: number, status: number, applicationStatusDate?: string, applicationStatusReason?: string, deferredToCourse?: string, deferredToBatch?: string, applicationWaitlistedDate?: string): Observable<any> {
    const payload: any = { 
      studentCourseApplicationId: studentCourseApplicationId, 
      studentCourseApplicationStatus: status 
    };
    if (applicationStatusDate) {
      payload.application_status_date = applicationStatusDate;
    }
    if (applicationStatusReason) {
      payload.reason = applicationStatusReason;
    }
    if (deferredToCourse) {
      payload.applicationDeferredToCourseId = deferredToCourse;
    }
    if (deferredToBatch) {
      payload.applicationDeferredToBatchId = deferredToBatch;
    }
    if (applicationWaitlistedDate) {
      payload.applicationWaitlistedDate = applicationWaitlistedDate;
    }
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/update-student-course-application-status`, payload, { responseType: 'text' as 'json' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateStudentStatus(studentId: number, status: number): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.students_journeys}/update-Student-status`, { id: studentId, status: status }, { responseType: 'text' as 'json' })
      .pipe(catchError(this.exceptionService.handleError));
  }
  /**
     * END: Student Refund Fee Services
     */

  @LoaderEnabled()
  getStudentContarctCollegeForms(companyId: number, instituteId: number, courseId: number, studentId?: number): Observable<any> {
    const studentParam = studentId ? `&studentId=${studentId}` : '';
    return this.http.get<any>(`${AppConstants.endpoint.student.college_forms}/reports?instituteId=${instituteId}&courseId=${courseId}${studentParam}&companyId=${companyId}`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getAllCollegeForms(instituteId: number): Observable<any> {
    // Fetch all forms for the institute (includes fee receipt forms without courseId)
    return this.http.get<any>(`${AppConstants.endpoint.student.college_forms}/reports?instituteId=${instituteId}`)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  downloadPaymentReceipt(paymentId: number, studentId: number, instituteId: number): Observable<Blob> {
    const url = AppConstants.endpoint.student.payment_receipt
      .replace('{studentId}', String(studentId))
      .replace('{paymentId}', String(paymentId))
      + `?institute_id=${instituteId}`;
    return this.http.get(url, { responseType: 'blob' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  emailPaymentReceipt(paymentId: number, studentId: number, instituteId: number): Observable<any> {
    const url = AppConstants.endpoint.student.email_payment_receipt
      .replace('{studentId}', String(studentId))
      .replace('{paymentId}', String(paymentId))
      + `?institute_id=${instituteId}`;
    return this.http.post<any>(url, {}, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  emailBirtReceipt(blob: Blob, filename: string, studentId: number, instituteId: number): Observable<any> {
    const url = AppConstants.endpoint.student.email_birt_receipt
      .replace('{studentId}', String(studentId))
      + `?institute_id=${instituteId}`;
    const formData = new FormData();
    formData.append('file', blob, filename);
    return this.http.post<any>(url, formData)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  downloadStudentContractDoc(payload: any): Observable<any> {
    const url = `${AppConstants.endpoint.student.college_forms}/generate`;
    const headers = new HttpHeaders({ "enctype": "multipart/form-data" });

    return this.http.post<any>(url, payload, { headers, responseType: 'blob' as 'json' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * Download a single v4 college form as PDF.
   * POST /college-forms/{formId}/generate-pdf?sca_id={scaId}
   */
  @LoaderEnabled()
  downloadSingleV4Form(formId: number, scaId: number): Observable<Blob> {
    const url = `${AppConstants.endpoint.student.college_forms_v4}/${formId}/generate-pdf?sca_id=${scaId}`;
    return this.http.post(url, {}, { responseType: 'blob' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * Download multiple v4 college forms as a ZIP file.
   * POST /college-forms/generate-pdf-zip
   * Body: { form_ids: number[], sca_id: number }
   */
  @LoaderEnabled()
  downloadMultipleV4Forms(formIds: number[], scaId: number): Observable<Blob> {
    const url = `${AppConstants.endpoint.student.college_forms_v4}/generate-pdf-zip`;
    const body: { form_ids: number[]; sca_id: number } = { form_ids: formIds, sca_id: scaId };
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post(url, body, { headers, responseType: 'blob' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getStudentCourseCredentials(payload: any): Observable<any> {
    let params = new HttpParams();
    params = params.append('institute_id', payload.institutionId);
    params = params.append('company_id', payload.companyId);
    params = params.append('course_id', payload.courseId);
    params = params.append('course_application_id', payload.courseApplicationId);
    params = params.append('student_id', payload.studentId);
    params = params.append('page', payload.page);
    params = params.append('size', payload.size);
    return this.http.get<any[]>(AppConstants.endpoint.moodle.getStudentCourseCredentials, { params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }


  public getPartners(): Observable<PartnerSummary[]> {
    return this.http.get<PartnerSummary[]>(AppConstants.endpoint.student.partners)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getStudentByUserId(userId: string): Observable<Student> {
    let params = new HttpParams();
    params = params.append('userId', userId.toString());
    return this.http.get<Student>(AppConstants.endpoint.student.base, { params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getStudent(studentId: number): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.get, studentId);
    return this.http.get<Student>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public createStudentWithGeneralInfo(value: any): Observable<Student> {
    return this.http.post<Student>(AppConstants.endpoint.student.base, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateStudentGeneralInfo(studentId: number, value: any): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.general_info, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateStudentEducationSummary(studentId: number, value: any): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.education_summary, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateStudentTestScore(studentId: number, value: any): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.test_score, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateStudentBackgroundInfo(studentId: number, value: any): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.background_info, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public addStudentDocument(studentId: number, value: StudentDocument): Observable<StudentDocument> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.documents, studentId);
    return this.http.post<StudentDocument>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteStudentDocument(studentId: number, studentDocumentId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.documents, studentId);
    url = url + '/' + studentDocumentId;
    return this.http.delete<any>(url, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getStudentChecklistAndValidate(studentId: number): Observable<any[]> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.checklist, studentId);
    return this.http.get<StudentChecklist[]>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public getStudentChecklist(studentId: number, instituteId: number, sourceId: number, courseApplicationId: number): Observable<any[]> {
    let params = new HttpParams();
    params = params.append('userRole', 'COURSE');
    params = params.append('sourceId', sourceId);
    params = params.append('instituteId', instituteId);
    params = params.append('courseApplicationId', courseApplicationId);
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.checklist_status, studentId);
    return this.http.get<StudentChecklist[]>(url, { params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  public approvePartnerProfile(studentId: number): Observable<Student> {
    let value = '';
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.approve_partner, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public revokeApprovePartnerProfile(studentId: number): Observable<Student> {
    let value = '';
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.revoke_approval_partner, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updatePartnerCompanyInfo(studentId: number, value: any): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.partner_company, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public updateBankingInfo(studentId: number, value: any): Observable<Student> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.banking_info, studentId);
    return this.http.put<Student>(url, value, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  public getUserStats(partnerId: number): Observable<UserStats> {
    let params = new HttpParams();
    if (partnerId != null) {
      params = params.append('partnerId', partnerId.toString());
    }

    return this.http.get<UserStats>(AppConstants.endpoint.student.stats, { params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  requestStudentDocumentUploadLink(payload: { 
    course_id?: number; 
    student_course_application_id: number; 
    expiry_hours?: number;
    documents?: any[];
    coordinator_notes?: string;
  }): Observable<any> {
    const body: any = {
      student_course_application_id: payload.student_course_application_id
    };
    // Include course_id if provided (for fetching checklist from backend)
    if (payload?.course_id) {
      body.course_id = payload.course_id;
    }
    // Include documents array if provided (for specific document selection)
    if (payload?.documents && payload.documents.length > 0) {
      body.documents = payload.documents;
    }
    // Only include expiry_hours if explicitly provided - otherwise let the API use institute setting
    if (payload?.expiry_hours) {
      body.expiry_hours = payload.expiry_hours;
    }
    if (payload?.coordinator_notes) {
      body.coordinator_notes = payload.coordinator_notes;
    }
    return this.http.post<any>(AppConstants.endpoint.student.request_document_upload_link, body, this.utilService.getHeaders())
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  sendDocumentsForSignature(payload: any): Observable<any> {
    return this.http.post<any>(AppConstants.endpoint.Docusign.sendDocumentsForSignature, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  getDocumentStatus(scaId: number): Observable<{ document_status: string; document_status_comments: any[] }> {
    const url = AppConstants.endpoint.student.document_status.replace('{id}', String(scaId));
    return this.http.get<{ document_status: string; document_status_comments: any[] }>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  updateDocumentStatus(scaId: number, documentStatus: string, comment: string): Observable<any> {
    const url = AppConstants.endpoint.student.document_status.replace('{id}', String(scaId));
    return this.http.put<any>(url, { document_status: documentStatus, comment })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  checkStudentDocumentStatus(studentId: number, courseApplicationId: number): Observable<any> {
    let url = AppConstants.endpoint.Docusign.checkStudentDocumentStatus
      .replace('{student_id}', String(studentId))
      .replace('{course_application_id}', String(courseApplicationId))
    return this.http.get<StudentChecklist[]>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  voidEnvelope(envelopeId: string, voidedReason: string = 'Document withdrawn by administrator'): Observable<any> {
    return this.http.post(AppConstants.endpoint.Docusign.voidEnvelope, {
      envelope_id: envelopeId,
      voided_reason: voidedReason
    }).pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  getAdmissionRequirements(studentId: number, courseApplicationId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.checklist, studentId);
    return this.http.get<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }
  
  @LoaderEnabled()
  syncStudentCourseCredentials(payload: any): Observable<any> {
    return this.http.post(AppConstants.endpoint.moodle.syncStudentCourseCredentials, payload, { responseType: 'text' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  generateT2202Form(payload: any): Observable<any> {
    const headers = new HttpHeaders({ "enctype": "multipart/form-data" });
    return this.http.post<any>(AppConstants.endpoint.report.t2202Form, payload, { headers, responseType: 'blob' as 'json' })
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  public getAllStudentWithEnrollments(instituteId: number): Observable<any> {
    let params = new HttpParams();
    params = params.append('instituteId', instituteId.toString());
    return this.http.get<any>(AppConstants.endpoint.student.studentWithEnrollments, { params: params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteStudent(studentId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.softDeleteStudent, studentId);
    return this.http.delete<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  public deleteStudentApplication(studentApplicationId: number): Observable<any> {
    let url = this.utilService.prepareUrl(AppConstants.endpoint.student.softDeleteStudentApplication, studentApplicationId);
    return this.http.delete<any>(url)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  transferStudentCourseApplication(payload: any): Observable<any> {
    return this.http.put<any>(`${AppConstants.endpoint.student.transfer_student_course_application}`, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  studentInstituteTransfer(payload: any): Observable<any> {
    return this.http.post<any>(`${AppConstants.endpoint.student.student_institute_transfer}`, payload)
      .pipe(catchError(this.exceptionService.handleError));
  }

  getTransferInstitutes(companyId: number): Observable<any> {
    return this.http.get<any>(`${AppConstants.endpoint.student.student_institute_transfer_institutes}`, {
      params: { companyId: companyId.toString() }
    }).pipe(catchError(this.exceptionService.handleError));
  }

  getTransferCourses(instituteId: number): Observable<any> {
    return this.http.get<any>(`${AppConstants.endpoint.student.transfer_courses}`, {
      params: { instituteId: instituteId.toString() }
    }).pipe(catchError(this.exceptionService.handleError));
  }

  getTransferBatches(courseId: number): Observable<any> {
    return this.http.get<any>(`${AppConstants.endpoint.student.transfer_batches}`, {
      params: { courseId: courseId.toString() }
    }).pipe(catchError(this.exceptionService.handleError));
  }

  /**
   * Update custom ID for a student journey
   */
  @LoaderEnabled()
  updateCustomId(studentCourseApplicationId: number, customId: string): Observable<any> {
    return this.http.put<any>(
      `${AppConstants.endpoint.student.students_journeys}/${studentCourseApplicationId}/custom-id?customId=${encodeURIComponent(customId)}`,
      null,
      { responseType: 'text' as 'json' }
    ).pipe(catchError(this.exceptionService.handleError));
  }

  markInterviewCompleted(scaId: number, value: boolean): Observable<any> {
    const url = AppConstants.endpoint.student.interview_completed.replace('{id}', scaId.toString());
    return this.http.patch<any>(`${url}?value=${value}`, {}).pipe(catchError(this.exceptionService.handleError));
  }

  getInterviewQuestionnaire(scaId: number): Observable<any> {
    const url = AppConstants.endpoint.student.interview_questionnaire.replace('{id}', scaId.toString());
    return this.http.get<any>(url).pipe(catchError(this.exceptionService.handleError));
  }

  createEvaluationCandidate(scaId: number): Observable<any> {
    return this.http.post<any>(
      AppConstants.endpoint.student.evaluation_candidates,
      { sca_id: scaId }
    ).pipe(catchError(this.exceptionService.handleError));
  }

  getEvaluationCandidate(scaId: number): Observable<any> {
    return this.http.get<any>(
      `${AppConstants.endpoint.student.evaluation_candidates}/${scaId}`
    ).pipe(catchError(this.exceptionService.handleError));
  }

  getEvaluationTests(): Observable<any> {
    return this.http.get<any>(
      AppConstants.endpoint.student.evaluation_tests
    ).pipe(catchError(this.exceptionService.handleError));
  }

  getEvaluationInvitations(scaId: number): Observable<any> {
    const url = AppConstants.endpoint.student.evaluation_invitations.replace('{scaId}', scaId.toString());
    return this.http.get<any>(url).pipe(catchError(this.exceptionService.handleError));
  }

  getEvaluationAssessmentReport(reportCode: string): Observable<any> {
    const url = AppConstants.endpoint.student.evaluation_report.replace('{reportCode}', reportCode);
    return this.http.get<any>(url).pipe(catchError(this.exceptionService.handleError));
  }

  inviteCandidateForEvaluation(payload: { sca_id: number; testId: string; expiresAt: string; validityPeriodDays: number }): Observable<any> {
    return this.http.post<any>(
      AppConstants.endpoint.student.evaluation_invite,
      payload
    ).pipe(catchError(this.exceptionService.handleError));
  }

  createInterviewQuestionnaire(scaId: number, payload: any): Observable<any> {
    const url = AppConstants.endpoint.student.interview_questionnaire.replace('{id}', scaId.toString());
    return this.http.post<any>(url, payload).pipe(catchError(this.exceptionService.handleError));
  }

  updateInterviewQuestionnaire(scaId: number, payload: any): Observable<any> {
    const url = AppConstants.endpoint.student.interview_questionnaire.replace('{id}', scaId.toString());
    return this.http.put<any>(url, payload).pipe(catchError(this.exceptionService.handleError));
  }

  @LoaderEnabled()
  updateAttachmentIsValid(attachmentID: number, isValid: boolean): Observable<any> {
    return this.http.put<any>(AppConstants.endpoint.attachment.updateIsValid, { attachmentID, isValid })
      .pipe(catchError(this.exceptionService.handleError));
  }

  updateAttachmentComments(attachmentID: number, isValid: boolean | null, l2IsValid: boolean | null, l1Comments: string | null, l2Comments: string | null, scaId?: number, studentId?: number, instituteId?: number): Observable<any> {
    return this.http.put<any>(AppConstants.endpoint.attachment.updateComments, { attachmentID, isValid, l2IsValid, l1Comments, l2Comments, scaId, studentId, instituteId })
      .pipe(catchError(this.exceptionService.handleError));
  }

  getMoodleCertificates(studentId: number, courseApplicationId: number, courseId: number, instituteId: number, companyId: number): Observable<any> {
    const params: any = { student_id: studentId, course_application_id: courseApplicationId, course_id: courseId, institute_id: instituteId, company_id: companyId };
    return this.http.get<any>(AppConstants.endpoint.student.moodle_credentials, { params })
      .pipe(catchError(this.exceptionService.handleError));
  }

  syncMoodleCertificates(email: string, studentId: number, courseApplicationId: number, courseId: number, instituteId: number, companyId: number): Observable<any> {
    const body = { email, student_id: studentId, courseApplicationId, course_id: courseId, institution_id: instituteId, company_id: companyId };
    return this.http.post<any>(AppConstants.endpoint.student.moodle_sync, body)
      .pipe(catchError(this.exceptionService.handleError));
  }
}
